import { useCallback, useEffect, useMemo, useState } from 'react';
import { Direction_Enum, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Fax } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { FaxV1 } from '@frontend/api-fax';

export const DEFAULT_FAX_HISTORY_PAGE_SIZE = 25;

type UseFaxHistoryArgs = {
  locationId: string;
  locationIds: string[];
  startAt?: string;
  endAt?: string;
  status?: Status_Enum;
  direction?: Direction_Enum;
  searchTerm?: string;
  pageSize?: number;
};

export const useFaxHistory = ({
  locationId,
  locationIds,
  startAt,
  endAt,
  status,
  direction,
  searchTerm,
  pageSize = DEFAULT_FAX_HISTORY_PAGE_SIZE,
}: UseFaxHistoryArgs) => {
  const [pageIndex, setPageIndex] = useState(0);
  const trimmedSearch = searchTerm?.trim() ?? '';
  const isSearching = trimmedSearch.length > 0;

  const listQuery = FaxV1.Queries.useListFaxesInfiniteQuery({
    request: {
      locationId,
      locationIds,
      startAt,
      endAt,
      status,
      direction,
      limit: pageSize,
    },
    options: {
      enabled: !isSearching,
    },
  });

  const searchQuery = FaxV1.Queries.useSearchFaxesQuery({
    request: {
      locationId,
      locationIds,
      query: trimmedSearch,
      startAt,
      endAt,
      status,
      direction,
      limit: pageSize,
    },
    options: {
      enabled: isSearching,
    },
  });

  const countQuery = FaxV1.Queries.useCountFaxesQuery({
    request: {
      locationId,
      locationIds,
      startAt,
      endAt,
      direction,
    },
    options: {
      enabled: !isSearching,
    },
  });

  const activeQuery = isSearching ? searchQuery : listQuery;
  const loadedPages = useMemo(() => activeQuery.data?.pages ?? [], [activeQuery.data?.pages]);

  useEffect(() => {
    setPageIndex(0);
  }, [locationId, locationIds, startAt, endAt, status, direction, trimmedSearch, pageSize]);

  const faxes: Fax[] = loadedPages[pageIndex]?.rows ?? [];

  const totalCount = isSearching ? undefined : countQuery.data?.faxCount;
  const totalPages = totalCount === undefined ? undefined : Math.max(1, Math.ceil(totalCount / pageSize));

  const hasNextPage =
    pageIndex < loadedPages.length - 1 ||
    (!!activeQuery.hasNextPage && loadedPages[pageIndex]?.rows?.length === pageSize);
  const hasPreviousPage = pageIndex > 0;

  const goToNextPage = useCallback(async () => {
    if (pageIndex < loadedPages.length - 1) {
      setPageIndex((prev) => prev + 1);
      return;
    }
    if (activeQuery.hasNextPage) {
      const result = await activeQuery.fetchNextPage();
      if (result.data && result.data.pages.length > pageIndex + 1) {
        setPageIndex((prev) => prev + 1);
      }
    }
  }, [activeQuery, loadedPages.length, pageIndex]);

  const goToPreviousPage = useCallback(() => {
    setPageIndex((prev) => Math.max(0, prev - 1));
  }, []);

  return {
    faxes,
    pageIndex,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    isSearching,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching || activeQuery.isFetchingNextPage,
    isError: activeQuery.isError,
    refetch: activeQuery.refetch,
  };
};
